import { Flatfile } from '@flatfile/api-internal'
import { Button, cn, Icon, IconName, Input, Text, useToast } from '@flatfile/baseline'
import { useCallback, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorBoundaryErrorPage } from '../../components/Shared/CustomErrorComponent'
import TrialBanner from '../../components/Shared/TrialBanner'
import { useEntitlements } from '../../hooks/useEntitlements'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import useResource from '../../resources/hooks/useResource'
import useResourceList from '../../resources/hooks/useResourceList'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import { logger } from '../../utils/logging'

type PolicyType = Flatfile.DataRetentionPolicyEnum
interface PolicyFormValues {
  type: PolicyType
  period: number
  id?: string
}

interface PolicyDisplayData {
  period: number
  type: PolicyType
}

const DEFAULT_PERIOD = 365
const DEFAULT_POLICY_TYPE: PolicyType = 'lastActivity'

interface PolicyTypeOptionProps {
  title: string
  description: string
  icon: IconName
  isSelected: boolean
  onClick: () => void
}

const PolicyTypeOption = ({ title, description, icon, isSelected, onClick }: PolicyTypeOptionProps) => (
  <div
    className={cn(
      'flex flex-col p-4 gap-2 hover:cursor-pointer rounded h-[86px] w-full min-w-[300px]',
      isSelected ? 'border border-black  ' : 'bg-gray-100'
    )}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    }}
    role='button'
    tabIndex={0}
  >
    <div className='flex'>
      <Icon name={icon} className='pr-1' size={18} />
      <Text variant='headline' emphasized>
        {title}
      </Text>
    </div>
    <Text variant='callout' color='muted'>
      {description}
    </Text>
  </div>
)

interface PolicyFormProps {
  policy: PolicyDisplayData
  submit: (values: PolicyFormValues) => void
  id?: string
}

const PolicyForm = ({ policy, submit, id }: PolicyFormProps) => {
  const [type, setType] = useState<PolicyType>(policy.type)
  const [periodString, setPeriodString] = useState(policy.period.toString())
  const [isDirty, setIsDirty] = useState(false)

  // Track when form values change from initial values
  useEffect(() => {
    // If this is a new policy (no id) consider the form dirty by default when values are valid
    if (!id) {
      setIsDirty(isPeriodValid(periodString))
    } else {
      setIsDirty(type !== policy.type || periodString !== policy.period.toString())
    }
  }, [type, periodString, policy.type, policy.period, id])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Parse to number on submit and ensure it's a valid number (minimum 1)
    const periodNumber = periodString === '' ? 0 : parseInt(periodString, 10)
    const validPeriod = isNaN(periodNumber) ? 1 : Math.max(1, periodNumber)

    submit({
      type,
      period: validPeriod,
      id,
    })
    setIsDirty(false)
  }

  // Check if period is valid (must be 1 or greater)
  const isPeriodValid = (value: string = periodString) => {
    if (value.trim() === '') return false
    const num = parseInt(value, 10)
    return !isNaN(num) && num >= 1
  }

  // Determine if the save button should be disabled
  const isSaveDisabled = !isDirty || !isPeriodValid()

  return (
    <form onSubmit={handleSubmit}>
      <Text>These data retention approaches hinge on time-based criteria.</Text>
      <div className='h-4' />
      <Text>
        Carefully assess your data retention needs and adopt a policy that aligns with your specific objectives and
        compliance obligations.
      </Text>
      <div className='h-8' />

      <div className='flex gap-[52px]'>
        {/* Policy Type Options - Left Side */}
        <div className='flex flex-col gap-4 w-1/2 max-w-[400px]'>
          <div className='bg-gray-50 rounded-md'>
            <PolicyTypeOption
              title='Time Since Last Activity'
              description='Retains data from the last interaction, deleting or archiving it after a period of inactivity.'
              icon='history'
              isSelected={type === 'lastActivity'}
              onClick={() => setType('lastActivity')}
            />
          </div>

          <div className='bg-gray-50 rounded-md'>
            <PolicyTypeOption
              title='Time Since Created'
              description='Keeps data for a set duration from its collection.'
              icon='trending-4'
              isSelected={type === 'sinceCreated'}
              onClick={() => setType('sinceCreated')}
            />
          </div>
        </div>

        {/* Form Controls - Right Side */}
        <div className='flex flex-col gap-6 w-1/2'>
          <div>
            <Text emphasized>Policy type</Text>
            <div className='h-2' />
            <div className='flex max-w-[300px]'>
              <Button
                variant={type === 'lastActivity' ? 'secondary' : 'ghost'}
                onClick={() => setType('lastActivity')}
                type='button'
                className={cn(
                  'rounded-r-none',
                  type === 'lastActivity' ? 'shadow-xs border-none' : 'text-text-default bg-overlay-darken-200'
                )}
              >
                Last activity
              </Button>
              <Button
                variant={type === 'sinceCreated' ? 'secondary' : 'ghost'}
                onClick={() => setType('sinceCreated')}
                type='button'
                className={cn(
                  'rounded-l-none',
                  type === 'sinceCreated' ? 'shadow-xs border-none' : 'text-text-default bg-overlay-darken-200'
                )}
              >
                Since created
              </Button>
            </div>
          </div>

          <div>
            <Text emphasized>Policy period</Text>
            <div className='h-2' />
            <div className='max-w-[300px] relative'>
              <Input
                type='text'
                min='1'
                placeholder='Enter period (minimum 1)'
                value={periodString}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPeriodString(e.target.value)}
              />
              <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
                <Text variant='footnote' emphasized>
                  days
                </Text>
              </div>
            </div>
            {periodString !== '' && !isPeriodValid() && (
              <div className='mt-1'>
                <Text variant='footnote' className='text-xxs'>
                  Period must be at least 1 day
                </Text>
              </div>
            )}
          </div>

          <div>
            <Button type='submit' disabled={isSaveDisabled} variant='primary'>
              Save policy
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

// Policy Fetcher component
interface PolicyFetcherProps {
  submit: (values: PolicyFormValues) => void
  environment: Flatfile.Environment
}

const PolicyFetcher = ({ submit, environment }: PolicyFetcherProps) => {
  const { ids } = useResourceList('DATA_RETENTION_POLICIES', {
    environmentId: environment.id,
  })

  const resource = useResource('DATA_RETENTION_POLICIES', ids[0])

  const policyDisplay: PolicyDisplayData = {
    period: resource?.period || DEFAULT_PERIOD,
    type: resource?.type || DEFAULT_POLICY_TYPE,
  }

  return <PolicyForm policy={policyDisplay} submit={submit} id={resource?.id} />
}

// Main content component
export function DataRetentionPageContent() {
  const { environment: activeEnvironment } = useActiveEnvironment()
  const [createPolicy] = useCreateResource('DATA_RETENTION_POLICIES')
  const [updatePolicy] = useUpdateResource('DATA_RETENTION_POLICIES')
  const { toast } = useToast()

  // Handle form submission
  const handleSubmit = useCallback(
    async (values: PolicyFormValues) => {
      if (!activeEnvironment) return

      try {
        const isUpdate = Boolean(values.id)
        const policyData = {
          ...values,
          period: Number(values.period),
          environmentId: activeEnvironment.id,
        }

        const policyRes = isUpdate ? await updatePolicy(values.id!, policyData) : await createPolicy(policyData)

        if (policyRes) {
          toast({
            title: 'Success',
            description: `Successfully ${isUpdate ? 'updated' : 'created'} your policy!`,
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Failed to update your policy.',
          })
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to update your policy.',
        })
        logger.error('failed to update data retention policy', { error })
      }
    },
    [activeEnvironment, createPolicy, updatePolicy, toast]
  )

  if (!activeEnvironment) return null

  return (
    <div className='w-full h-full flex flex-col overflow-auto p-3'>
      <div className='p-3 flex justify-between'>
        <Text variant='title2'>Data Retention</Text>
        <TrialBanner />
      </div>
      <div className='px-3 min-w-[616px]'>
        <PolicyFetcher submit={handleSubmit} environment={activeEnvironment} />
      </div>
    </div>
  )
}

// Main page component with entitlement check
export const DataRetentionPage = () => {
  const { hasEntitlement } = useEntitlements()
  const hasDataRetentionAccess = hasEntitlement('dataRetention')

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <ErrorBoundaryErrorPage
          error={error}
          page='dataRetention.page.tsx'
          message='There was an error loading your retention policy data.'
        />
      )}
    >
      {hasDataRetentionAccess ? (
        <DataRetentionPageContent />
      ) : (
        <Text>
          Data Retention is not available on your account. Please contact{' '}
          <a href='mailto:support@flatfile.com'>support@flatfile.com</a>.
        </Text>
      )}
    </ErrorBoundary>
  )
}
